import { BaseService } from './BaseService';
import { 
  Fund, 
  FundBalance, 
  AccountType,
  ValidationResult,
  PaymentRequest
} from '../types/accounting';
import { 
  ValidationError, 
  BusinessRuleError 
} from '../utils/errors';
import { Decimal } from 'decimal.js';
import { PoolClient } from 'pg';
import { DateTime } from 'luxon';
import { QueryResult, QueryResultRow } from 'pg';
import axios from 'axios';
import {
  GrantRequirement,
  FinancialSummary,
  ComplianceRequirement,
  BudgetConstraint
} from '../types/accounting';
import { FundTotals } from '../types/accounting';
import { FiscalYear } from '../types/accounting';

interface FundRestriction {
  type: 'time' | 'purpose' | 'perpetual';
  description: string;
  conditions: any[];
  start_date?: Date;
  end_date?: Date;
  reporting_requirements?: any[];
}

interface ComplianceResult {
  valid: boolean;
  errors: string[];
  warnings?: string[];
}

interface FundSummary {
  opening_balance: number;
  closing_balance: number;
  total_income: number;
  total_expenses: number;
  net_change: number;
  category_totals: {
    [key: string]: {
      debits: number;
      credits: number;
      net: number;
    };
  };
  compliance_status: {
    violations: number;
    warnings: number;
    status: 'compliant' | 'warning' | 'violation';
  };
}

export class FundManagementService extends BaseService {
  private api = axios.create({
    baseURL: process.env.API_BASE_URL,
    timeout: 10000,
  });

  async createFund(
    causeId: string,
    fundData: Partial<Fund>,
    restrictions?: FundRestriction[],
    grantRequirements?: GrantRequirement[]
  ): Promise<Fund> {
    this.validateRequired(fundData, ['name', 'type', 'code']);

    return await this.withTransaction(async (client) => {
      // Create fund record
      const fundQuery = `
        INSERT INTO sponsfi.funds (
          cause_id,
          name,
          code,
          type,
          restriction_type,
          start_date,
          end_date,
          description,
          settings,
          status
        ) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, 'active')
        RETURNING *
      `;

      const fundResult = await this.executeQueryWithParams<Fund>(
        fundQuery,
        [
          causeId,
          fundData.name,
          fundData.code,
          fundData.type,
          fundData.restriction_type,
          fundData.start_date,
          fundData.end_date,
          fundData.description,
          JSON.stringify({
            restrictions: restrictions || [],
            grant_requirements: grantRequirements || []
          })
        ],
        client
      );

      const fund = fundResult.rows[0];

      // Create fund restrictions if provided
      if (restrictions?.length) {
        await this.createFundRestrictions(client, fund.id, restrictions);
      }

      // Set up grant tracking if applicable
      if (grantRequirements?.length) {
        await this.setupGrantRequirements(client, fund.id, grantRequirements);
      }

      // Initialize fund balances
      await this.initializeFundBalances(client, fund.id);

      // Create compliance monitoring triggers
      await this.setupComplianceMonitoring(client, fund.id, {
        restrictions,
        grantRequirements
      });

      return fund;
    });
  }

  async getFundBalance(
    fundId: string,
    asOfDate: Date = new Date()
  ): Promise<FundBalance> {
    const query = `
      WITH fund_transactions AS (
        SELECT 
          je.fund_id,
          je.debit,
          je.credit,
          j.entry_date,
          j.status,
          a.type as account_type
        FROM sponsfi.journal_entries je
        JOIN sponsfi.journals j ON je.journal_id = j.id
        JOIN sponsfi.accounts a ON je.account_id = a.id
        WHERE je.fund_id = $1
          AND j.entry_date <= $2
          AND j.status = 'posted'
      ),
      balance_calc AS (
        SELECT
          fund_id,
          SUM(CASE 
            WHEN account_type IN ('asset', 'expense') THEN debit - credit
            ELSE credit - debit
          END) as current_balance,
          SUM(CASE 
            WHEN account_type = 'asset' THEN debit - credit
            ELSE 0
          END) as available_balance
        FROM fund_transactions
        GROUP BY fund_id
      )
      SELECT 
        f.id as fund_id,
        f.cause_id,
        COALESCE(bc.current_balance, 0) as current_balance,
        COALESCE(bc.available_balance, 0) as available_balance,
        COALESCE((
          SELECT SUM(CASE 
            WHEN fr.type = 'perpetual' THEN bc.current_balance
            WHEN fr.end_date > CURRENT_DATE THEN bc.current_balance
            ELSE 0
          END)
          FROM sponsfi.fund_restrictions fr
          WHERE fr.fund_id = f.id
        ), 0) as restricted_balance,
        $2::date as as_of_date
      FROM sponsfi.funds f
      LEFT JOIN balance_calc bc ON bc.fund_id = f.id
      WHERE f.id = $1
    `;

    const result = await this.executeQueryWithParams<FundBalance>(
      query,
      [fundId, asOfDate]
    );

    if (result.rowCount === 0) {
      throw new ValidationError('Fund not found');
    }

    return result.rows[0];
  }

  async getFundDetails(fundId: string): Promise<Fund> {
    const query = `
      SELECT 
        f.*,
        COALESCE(
          jsonb_agg(
            DISTINCT jsonb_build_object(
              'type', fr.type,
              'description', fr.description,
              'conditions', fr.conditions,
              'start_date', fr.start_date,
              'end_date', fr.end_date
            )
          ) FILTER (WHERE fr.id IS NOT NULL),
          '[]'::jsonb
        ) as restrictions,
        COALESCE(
          jsonb_agg(
            DISTINCT jsonb_build_object(
              'type', gr.type,
              'description', gr.description,
              'due_date', gr.due_date,
              'reporting_frequency', gr.reporting_frequency,
              'compliance_requirements', gr.compliance_requirements,
              'budget_constraints', gr.budget_constraints
            )
          ) FILTER (WHERE gr.id IS NOT NULL),
          '[]'::jsonb
        ) as grant_requirements
      FROM sponsfi.funds f
      LEFT JOIN sponsfi.fund_restrictions fr ON fr.fund_id = f.id
      LEFT JOIN sponsfi.grant_requirements gr ON gr.fund_id = f.id
      WHERE f.id = $1
      GROUP BY f.id
    `;

    const result = await this.executeQueryWithParams<Fund>(query, [fundId]);

    if (result.rowCount === 0) {
      throw new Error(`Fund ${fundId} not found`);
    }

    const fund = result.rows[0];

    // Transform dates from strings to Date objects
    return {
      ...fund,
      start_date: fund.start_date ? new Date(fund.start_date) : undefined,
      end_date: fund.end_date ? new Date(fund.end_date) : undefined,
      created_at: new Date(fund.created_at),
      updated_at: new Date(fund.updated_at),
      restrictions: fund.restrictions.map((r: any) => ({
        ...r,
        start_date: r.start_date ? new Date(r.start_date) : undefined,
        end_date: r.end_date ? new Date(r.end_date) : undefined
      })),
      grant_requirements: fund.grant_requirements.map((gr: any) => ({
        ...gr,
        due_date: new Date(gr.due_date)
      }))
    };
  }

  async validateFundTransaction(
    fundId: string,
    amount: number,
    transactionType: string
  ): Promise<ValidationResult> {
    const errors = [];
    const fund = await this.getFundDetails(fundId);

    // Check fund status
    if (fund.status !== 'active') {
      errors.push({
        field: 'fund',
        message: 'Fund is not active'
      });
    }

    // Check time restrictions
    if (fund.end_date && new Date(fund.end_date) < new Date()) {
      errors.push({
        field: 'time_restriction',
        message: 'Fund has expired'
      });
    }

    // Check available balance for withdrawals
    if (transactionType === 'withdrawal') {
      const balance = await this.getFundBalance(fundId);
      if (new Decimal(balance.available_balance).lessThan(amount)) {
        errors.push({
          field: 'balance',
          message: 'Insufficient available balance'
        });
      }
    }

    // Check grant requirements
    const grantRequirements = await this.getGrantRequirements(fundId);
    if (grantRequirements.length > 0) {
      const requirementValidation = await this.validateGrantRequirements(
        fundId,
        amount,
        transactionType
      );
      errors.push(...requirementValidation.errors);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  async generateFundReport(
    fundId: string,
    startDate: Date,
    endDate: Date
  ): Promise<any> {
    const query = `
      WITH fund_activity AS (
        SELECT 
          j.entry_date,
          je.debit,
          je.credit,
          je.memo,
          a.code as account_code,
          a.name as account_name,
          a.type as account_type
        FROM sponsfi.journal_entries je
        JOIN sponsfi.journals j ON je.journal_id = j.id
        JOIN sponsfi.accounts a ON je.account_id = a.id
        WHERE je.fund_id = $1
          AND j.entry_date BETWEEN $2 AND $3
          AND j.status = 'posted'
        ORDER BY j.entry_date
      )
      SELECT 
        to_char(entry_date, 'YYYY-MM') as period,
        account_type,
        SUM(debit) as total_debits,
        SUM(credit) as total_credits,
        SUM(CASE 
          WHEN account_type IN ('asset', 'expense') THEN debit - credit
          ELSE credit - debit
        END) as net_change
      FROM fund_activity
      GROUP BY period, account_type
      ORDER BY period, account_type
    `;

    const result = await this.executeQueryWithParams(
      query,
      [fundId, startDate, endDate]
    );

    return {
      fund_id: fundId,
      period_start: startDate,
      period_end: endDate,
      activity: result.rows,
      summary: await this.generateFundSummary(fundId, startDate, endDate)
    };
  }

  private async createFundRestrictions(
    client: any,
    fundId: string,
    restrictions: FundRestriction[]
  ): Promise<void> {
    const query = `
      INSERT INTO sponsfi.fund_restrictions (
        fund_id,
        type,
        description,
        conditions,
        start_date,
        end_date
      ) VALUES ($1, $2, $3, $4, $5, $6)
    `;

    for (const restriction of restrictions) {
      await this.executeQueryWithParams(
        query,
        [
          fundId,
          restriction.type,
          restriction.description,
          JSON.stringify(restriction.conditions),
          restriction.start_date,
          restriction.end_date
        ],
        client
      );
    }
  }

  private async setupGrantRequirements(
    client: any,
    fundId: string,
    requirements: GrantRequirement[]
  ): Promise<void> {
    const query = `
      INSERT INTO sponsfi.grant_requirements (
        fund_id,
        type,
        description,
        due_date,
        reporting_frequency,
        compliance_requirements,
        budget_constraints,
        status
      ) VALUES ($1, $2, $3, $4, $5, $6, $7, 'active')
    `;

    for (const req of requirements) {
      await this.executeQueryWithParams(
        query,
        [
          fundId,
          req.type,
          req.description,
          req.due_date,
          req.reporting_frequency,
          JSON.stringify(req.compliance_requirements),
          JSON.stringify(req.budget_constraints)
        ],
        client
      );

      // Set up automated reporting schedules
      await this.createReportingSchedule(client, fundId, req);
    }
  }

  private async setupComplianceMonitoring(
    client: PoolClient,
    fundId: string,
    config: {
      restrictions?: FundRestriction[],
      grantRequirements?: GrantRequirement[]
    }
  ): Promise<void> {
    // Create compliance monitoring rules
    const monitoringQuery = `
      INSERT INTO sponsfi.compliance_monitoring (
        fund_id,
        monitor_type,
        criteria,
        frequency,
        notification_rules,
        status
      ) VALUES ($1, $2, $3, $4, $5, 'active')
    `;

    // Set up restriction monitoring
    if (config.restrictions?.length) {
      for (const restriction of config.restrictions) {
        await this.executeQueryWithParams(
          monitoringQuery,
          [
            fundId,
            'restriction',
            JSON.stringify({
              type: restriction.type,
              conditions: restriction.conditions
            }),
            'real-time',
            JSON.stringify({
              threshold_alerts: true,
              expiration_notices: true,
              compliance_reports: true
            })
          ],
          client
        );
      }
    }

    // Set up grant requirement monitoring
    if (config.grantRequirements?.length) {
      for (const req of config.grantRequirements) {
        await this.executeQueryWithParams(
          monitoringQuery,
          [
            fundId,
            'grant_requirement',
            JSON.stringify(req.compliance_requirements),
            req.reporting_frequency,
            JSON.stringify({
              due_date_reminders: true,
              budget_alerts: true,
              compliance_violations: true
            })
          ],
          client
        );
      }
    }
  }

  async generateForm990Schedule(
    fundId: string,
    taxYear: string
  ): Promise<any> {
    // First get the fiscal year dates
    const fiscalYearQuery = `
      SELECT fiscal_year_start, fiscal_year_end
      FROM sponsfi.fiscal_years
      WHERE fund_id = $1 AND tax_year = $2::integer
    `;

    const fiscalYear = await this.executeQueryWithParams<FiscalYear>(
      fiscalYearQuery,
      [fundId, taxYear]
    );

    if (!fiscalYear.rows[0]) {
      throw new Error(`No fiscal year found for fund ${fundId} and tax year ${taxYear}`);
    }

    const { fiscal_year_start, fiscal_year_end } = fiscalYear.rows[0];

    const query = `
      WITH fund_transactions AS (
        SELECT 
          je.fund_id,
          je.account_id,
          je.debit,
          je.credit,
          j.entry_date,
          a.type as account_type,
          a.category,
          a.subcategory
        FROM sponsfi.journal_entries je
        JOIN sponsfi.journals j ON je.journal_id = j.id
        JOIN sponsfi.accounts a ON je.account_id = a.id
        WHERE je.fund_id = $1
          AND EXTRACT(YEAR FROM j.entry_date) = $2::integer
          AND j.status = 'posted'
      ),
      category_totals AS (
        SELECT 
          category,
          subcategory,
          SUM(CASE 
            WHEN account_type IN ('asset', 'expense') THEN debit - credit
            ELSE credit - debit
          END) as amount
        FROM fund_transactions
        GROUP BY category, subcategory
      )
      SELECT 
        f.name as fund_name,
        f.type as fund_type,
        f.restriction_type,
        ct.*,
        fr.type as restriction_type,
        fr.description as restriction_description,
        gr.compliance_requirements
      FROM sponsfi.funds f
      LEFT JOIN category_totals ct ON true
      LEFT JOIN sponsfi.fund_restrictions fr ON fr.fund_id = f.id
      LEFT JOIN sponsfi.grant_requirements gr ON gr.fund_id = f.id
      WHERE f.id = $1
    `;

    const result = await this.executeQueryWithParams(
      query,
      [fundId, taxYear]
    );

    return this.formatForm990Data(fundId, {
      start: new Date(fiscal_year_start),
      end: new Date(fiscal_year_end)
    });
  }

  private async createReportingSchedule(
    client: any,
    fundId: string,
    requirement: GrantRequirement
  ): Promise<void> {
    const scheduleQuery = `
      INSERT INTO sponsfi.reporting_schedule (
        fund_id,
        requirement_id,
        report_type,
        frequency,
        next_due_date,
        notification_days,
        status
      ) VALUES ($1, $2, $3, $4, $5, $6, 'active')
    `;

    // Calculate reporting dates based on frequency
    const reportingDates = this.calculateReportingDates(
      requirement.due_date,
      requirement.reporting_frequency
    );

    for (const date of reportingDates) {
      await this.executeQueryWithParams(
        scheduleQuery,
        [
          fundId,
          requirement.id,
          requirement.type,
          requirement.reporting_frequency,
          date,
          [30, 14, 7, 1] // Notification days before due date
        ],
        client
      );
    }
  }

  private async validateGrantRequirements(
    fundId: string,
    amount: number,
    transactionType: string
  ): Promise<ValidationResult> {
    const errors = [];
    const requirements = await this.getGrantRequirements(fundId);

    for (const req of requirements) {
      // Check budget constraints
      if (req.budget_constraints) {
        const budgetValidation = await this.validateBudgetConstraints(
          fundId,
          amount,
          transactionType,
          req.budget_constraints
        );
        if (!budgetValidation.isValid) {
          errors.push(...budgetValidation.errors);
        }
      }

      // Check compliance requirements
      if (req.compliance_requirements) {
        const complianceValidation = await this.validateComplianceRequirements(
          fundId,
          req.compliance_requirements
        );
        if (!complianceValidation.isValid) {
          errors.push(...complianceValidation.errors);
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private async formatForm990Data(fundId: string, period: { start: Date; end: Date }): Promise<any> {
    // Transform data into IRS Form 990 format
    const form990Data = {
      part_i: {
        revenue_summary: await this.calculateRevenueSummary(fundId, period),
        expense_summary: await this.calculateExpenseSummary(fundId, period)
      },
      part_ii: {
        program_service_accomplishments: await this.extractProgramServices(fundId)
      },
      part_iv: {
        balance_sheet: await this.formatBalanceSheet(fundId)
      },
      schedule_a: await this.formatScheduleA(fundId),
      schedule_b: await this.formatScheduleB(fundId),
      // Additional schedules as needed
    };

    return form990Data;
  }

  // Add getGrantRequirements method
  async getGrantRequirements(fundId: string): Promise<GrantRequirement[]> {
    const query = `
      SELECT 
        id,
        fund_id,
        type,
        description,
        due_date,
        reporting_frequency,
        compliance_requirements,
        budget_constraints,
        status
      FROM sponsfi.grant_requirements
      WHERE fund_id = $1
        AND status = 'active'
    `;

    const result = await this.executeQueryWithParams<GrantRequirement>(
      query,
      [fundId]
    );
    return result.rows;
  }

  private async initializeFundBalances(client: PoolClient, fundId: string): Promise<void> {
    const balanceQuery = `
      INSERT INTO fund_balances (
        fund_id,
        current_balance,
        available_balance,
        pending_balance,
        reserved_balance,
        last_updated
      ) VALUES ($1, 0, 0, 0, 0, NOW())
    `;

    await this.executeQueryWithParams(balanceQuery, [fundId], client);
  }

  protected async executeQuery<T>(query: string, params?: any[]): Promise<T> {
    return await super.executeQuery(query, params) as unknown as T;
  }

  protected async executeQueryWithParams<T extends QueryResultRow>(
    query: string,
    params: any[],
    client?: PoolClient
  ): Promise<QueryResult<T>> {
    if (client) {
      // If client is provided, we're in a transaction
      return await client.query<T>(query, params);
    } else {
      // Otherwise use our BaseService executeQuery
      return await this.executeQuery(query, params);
    }
  }

  async getAllPaymentRequests(): Promise<PaymentRequest[]> {
    try {
      const response = await this.api.get('/payment-requests');
      return response.data.map((request: any) => ({
        ...request,
        requestDate: new Date(request.requestDate),
        vendor: {
          ...request.vendor,
          w9Status: request.vendor.w9Status || 'pending',
          ofacStatus: request.vendor.ofacStatus || 'clear'
        },
        documents: request.documents || [],
        compliance: {
          checks: request.compliance?.checks || [],
          restrictions: request.compliance?.restrictions || []
        },
        approvals: (request.approvals || []).map((approval: any) => ({
          ...approval,
          date: new Date(approval.date)
        })),
        history: (request.history || []).map((entry: any) => ({
          ...entry,
          date: new Date(entry.date)
        }))
      }));
    } catch (error) {
      console.error('Failed to fetch all payment requests:', error);
      throw new Error('Failed to fetch payment requests');
    }
  }

  async getFundPaymentRequests(fundId: string): Promise<PaymentRequest[]> {
    try {
      const response = await this.api.get(`/funds/${fundId}/payment-requests`);
      return response.data.map((request: any) => ({
        ...request,
        requestDate: new Date(request.requestDate),
        vendor: {
          ...request.vendor,
          w9Status: request.vendor.w9Status || 'pending',
          ofacStatus: request.vendor.ofacStatus || 'clear'
        },
        documents: request.documents || [],
        compliance: {
          checks: request.compliance?.checks || [],
          restrictions: request.compliance?.restrictions || []
        },
        approvals: (request.approvals || []).map((approval: any) => ({
          ...approval,
          date: new Date(approval.date)
        })),
        history: (request.history || []).map((entry: any) => ({
          ...entry,
          date: new Date(entry.date)
        }))
      }));
    } catch (error) {
      console.error(`Failed to fetch payment requests for fund ${fundId}:`, error);
      throw new Error('Failed to fetch fund payment requests');
    }
  }

  async validatePaymentCompliance(requestId: string): Promise<ComplianceResult> {
    try {
      const response = await this.api.post(`/payment-requests/${requestId}/validate`, {
        validateCompliance: true,
        validateBudget: true,
        validateVendor: true
      });

      return {
        valid: response.data.valid,
        errors: response.data.errors || [],
        warnings: response.data.warnings || []
      };
    } catch (error) {
      console.error(`Failed to validate payment compliance for request ${requestId}:`, error);
      return {
        valid: false,
        errors: ['Failed to validate payment compliance. Please try again.'],
        warnings: []
      };
    }
  }

  // Helper method to check budget availability
  async checkBudgetAvailability(requestId: string): Promise<{
    available: boolean;
    remaining: number;
    required: number;
  }> {
    try {
      const response = await this.api.get(`/payment-requests/${requestId}/budget-check`);
      return {
        available: response.data.available,
        remaining: response.data.remaining,
        required: response.data.required
      };
    } catch (error) {
      console.error(`Failed to check budget availability for request ${requestId}:`, error);
      throw new Error('Failed to check budget availability');
    }
  }

  private async generateFundSummary(
    fundId: string,
    startDate: Date,
    endDate: Date
  ): Promise<FundSummary> {
    const query = `
      WITH fund_transactions AS (
        SELECT 
          je.account_id,
          je.debit,
          je.credit,
          j.entry_date,
          a.type as account_type,
          a.category,
          a.subcategory
        FROM sponsfi.journal_entries je
        JOIN sponsfi.journals j ON je.journal_id = j.id
        JOIN sponsfi.accounts a ON je.account_id = a.id
        WHERE je.fund_id = $1
          AND j.entry_date BETWEEN $2 AND $3
          AND j.status = 'posted'
      ),
      opening_balance AS (
        SELECT 
          COALESCE(SUM(CASE 
            WHEN account_type IN ('asset', 'expense') THEN debit - credit
            ELSE credit - debit
          END), 0) as balance
        FROM sponsfi.journal_entries je
        JOIN sponsfi.journals j ON je.journal_id = j.id
        JOIN sponsfi.accounts a ON je.account_id = a.id
        WHERE je.fund_id = $1
          AND j.entry_date < $2
          AND j.status = 'posted'
      ),
      category_totals AS (
        SELECT 
          category,
          SUM(debit) as total_debits,
          SUM(credit) as total_credits,
          SUM(CASE 
            WHEN account_type IN ('asset', 'expense') THEN debit - credit
            ELSE credit - debit
          END) as net_amount
        FROM fund_transactions
        GROUP BY category
      ),
      compliance_checks AS (
        SELECT 
          COUNT(*) FILTER (WHERE severity = 'violation') as violations,
          COUNT(*) FILTER (WHERE severity = 'warning') as warnings
        FROM sponsfi.compliance_logs
        WHERE fund_id = $1
          AND check_date BETWEEN $2 AND $3
      )
      SELECT 
        (SELECT balance FROM opening_balance) as opening_balance,
        COALESCE(SUM(CASE 
          WHEN account_type IN ('asset', 'expense') THEN debit - credit
          ELSE credit - debit
        END), 0) as net_change,
        COALESCE(SUM(CASE 
          WHEN account_type = 'income' THEN credit - debit
          ELSE 0
        END), 0) as total_income,
        COALESCE(SUM(CASE 
          WHEN account_type = 'expense' THEN debit - credit
          ELSE 0
        END), 0) as total_expenses,
        (SELECT jsonb_object_agg(
          category,
          jsonb_build_object(
            'debits', total_debits,
            'credits', total_credits,
            'net', net_amount
          )
        ) FROM category_totals) as category_totals,
        (SELECT jsonb_build_object(
          'violations', violations,
          'warnings', warnings,
          'status', CASE 
            WHEN violations > 0 THEN 'violation'
            WHEN warnings > 0 THEN 'warning'
            ELSE 'compliant'
          END
        ) FROM compliance_checks) as compliance_status
      FROM fund_transactions
    `;

    const result = await this.executeQueryWithParams(query, [
      fundId,
      startDate,
      endDate
    ]);

    const summary = result.rows[0] as {
      opening_balance: string;
      net_change: string;
      total_income: string;
      total_expenses: string;
      category_totals: Record<string, any>;
      compliance_status: {
        violations: number;
        warnings: number;
        status: 'compliant' | 'warning' | 'violation';
      };
    };

    const opening_balance = Number(summary.opening_balance) || 0;
    const net_change = Number(summary.net_change) || 0;

    return {
      opening_balance,
      closing_balance: opening_balance + net_change,
      total_income: Number(summary.total_income) || 0,
      total_expenses: Number(summary.total_expenses) || 0,
      net_change,
      category_totals: summary.category_totals || {},
      compliance_status: summary.compliance_status || {
        violations: 0,
        warnings: 0,
        status: 'compliant'
      }
    };
  }

  private calculateReportingDates(dueDate: Date, frequency: string): Date[] {
    const dates: Date[] = [];
    const startDate = DateTime.fromJSDate(dueDate);
    
    switch (frequency.toLowerCase()) {
      case 'annual':
        dates.push(dueDate);
        break;
        
      case 'semi-annual':
        dates.push(dueDate);
        dates.push(startDate.minus({ months: 6 }).toJSDate());
        break;
        
      case 'quarterly':
        dates.push(dueDate);
        for (let i = 1; i <= 3; i++) {
          dates.push(startDate.minus({ months: i * 3 }).toJSDate());
        }
        break;
        
      case 'monthly':
        dates.push(dueDate);
        for (let i = 1; i <= 11; i++) {
          dates.push(startDate.minus({ months: i }).toJSDate());
        }
        break;
        
      default:
        dates.push(dueDate);
    }

    // Sort dates in ascending order
    return dates.sort((a, b) => a.getTime() - b.getTime());
  }

  private async validateBudgetConstraints(
    fundId: string,
    amount: number,
    transactionType: string,
    constraints: BudgetConstraint[]
  ): Promise<ValidationResult> {
    const errors = [];
    const currentTotals = await this.getFundTotals(fundId);

    for (const constraint of constraints) {
      if (constraint.max_amount && (currentTotals[constraint.category] + amount) > constraint.max_amount) {
        errors.push({
          field: 'amount',
          message: `Exceeds maximum amount for ${constraint.category}`
        });
      }

      if (constraint.percentage) {
        const totalBudget = await this.getFundBudget(fundId);
        const maxAmount = totalBudget * (constraint.percentage / 100);
        if ((currentTotals[constraint.category] + amount) > maxAmount) {
          errors.push({
            field: 'amount',
            message: `Exceeds percentage allocation for ${constraint.category}`
          });
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private async validateComplianceRequirements(
    fundId: string,
    requirements: ComplianceRequirement[]
  ): Promise<ValidationResult> {
    const errors = [];

    for (const req of requirements) {
      if (req.required) {
        const isCompliant = await this.checkCompliance(fundId, req);
        if (!isCompliant) {
          errors.push({
            field: 'compliance',
            message: `Failed compliance check: ${req.description}`
          });
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private async calculateRevenueSummary(fundId: string, period: { start: Date; end: Date }): Promise<FinancialSummary> {
    // Implementation for revenue calculation
    return this.calculateFinancialSummary(fundId, period, 'revenue');
  }

  private async calculateExpenseSummary(fundId: string, period: { start: Date; end: Date }): Promise<FinancialSummary> {
    // Implementation for expense calculation
    return this.calculateFinancialSummary(fundId, period, 'expense');
  }

  private async extractProgramServices(fundId: string): Promise<any[]> {
    const query = `
      SELECT 
        ps.id,
        ps.name,
        ps.description,
        ps.total_expenses,
        ps.beneficiaries_count,
        ps.outcomes
      FROM sponsfi.program_services ps
      WHERE ps.fund_id = $1
    `;
    
    const result = await this.executeQueryWithParams(query, [fundId]);
    return result.rows;
  }

  private async formatBalanceSheet(fundId: string): Promise<any> {
    // Implementation for balance sheet formatting
    return this.generateFinancialReport(fundId, 'balance_sheet');
  }

  private async formatScheduleA(fundId: string): Promise<any> {
    // Implementation for Schedule A formatting
    return this.generateFinancialReport(fundId, 'schedule_a');
  }

  private async formatScheduleB(fundId: string): Promise<any> {
    // Implementation for Schedule B formatting
    return this.generateFinancialReport(fundId, 'schedule_b');
  }

  private async generateFinancialReport(fundId: string, reportType: string): Promise<any> {
    const query = `
      SELECT * FROM sponsfi.generate_financial_report($1, $2)
    `;
    
    const result = await this.executeQueryWithParams(query, [fundId, reportType]);
    return result.rows[0];
  }

  async getFunds(): Promise<Array<{ id: string; name: string }>> {
    try {
      const query = `
        SELECT id, name 
        FROM sponsfi.funds 
        WHERE status = 'active'
        ORDER BY name ASC
      `;
      
      const result = await this.executeQueryWithParams(query, []);
      return result.rows.map((row: unknown) => {
        const fund = row as { id: string; name: string };
        return {
          id: fund.id,
          name: fund.name
        };
      });
    } catch (error) {
      console.error('Error fetching funds:', error);
      throw new Error('Failed to fetch funds');
    }
  }

  private async calculateFinancialSummary(
    fundId: string, 
    period: { start: Date; end: Date },
    type: 'revenue' | 'expense'
  ): Promise<FinancialSummary> {
    const query = `
      WITH transactions AS (
        SELECT 
          je.account_id,
          a.category,
          a.subcategory,
          CASE 
            WHEN $3 = 'revenue' THEN je.credit - je.debit
            ELSE je.debit - je.credit
          END as amount
        FROM sponsfi.journal_entries je
        JOIN sponsfi.journals j ON je.journal_id = j.id
        JOIN sponsfi.accounts a ON je.account_id = a.id
        WHERE je.fund_id = $1
          AND j.entry_date BETWEEN $2 AND $3
          AND j.status = 'posted'
          AND a.type = $3
      )
      SELECT 
        COALESCE(SUM(amount), 0) as total,
        jsonb_object_agg(
          category,
          COALESCE(SUM(amount), 0)
        ) as categories,
        jsonb_object_agg(
          category,
          jsonb_object_agg(
            subcategory,
            COALESCE(SUM(amount), 0)
          )
        ) as subcategories
      FROM transactions
      GROUP BY category
    `;

    const result = await this.executeQueryWithParams(query, [
      fundId,
      period.start,
      period.end,
      type
    ]);

    const row = result.rows[0] as {
      total: string | number;
      categories: Record<string, number>;
      subcategories: Record<string, Record<string, number>>;
    } || {
      total: 0,
      categories: {},
      subcategories: {}
    };

    return {
      total: Number(row.total),
      categories: row.categories,
      subcategories: row.subcategories
    };
  }

  async getFundActivity(fundId: string, period: { start: Date; end: Date }): Promise<any> {
    const query = `
      SELECT * FROM sponsfi.fund_activity
      WHERE fund_id = $1
      AND activity_date BETWEEN $2 AND $3
      ORDER BY activity_date DESC
    `;

    const result = await this.executeQueryWithParams(query, [
      fundId,
      period.start,
      period.end
    ]);

    return {
      fund_id: fundId,
      period_start: period.start,
      period_end: period.end,
      activity: result.rows,
      summary: await this.generateFundSummary(fundId, period.start, period.end)
    };
  }

  async getForm990Data(fundId: string, taxYear: number): Promise<any> {
    const query = `
      SELECT fiscal_year_start, fiscal_year_end
      FROM sponsfi.fiscal_years
      WHERE fund_id = $1 AND tax_year = $2
    `;

    const result = await this.executeQueryWithParams<FiscalYear>(
      query,
      [fundId, taxYear]
    );

    const fiscalYear = result.rows[0];
    if (!fiscalYear) {
      throw new Error(`No fiscal year data found for fund ${fundId} and tax year ${taxYear}`);
    }

    return this.formatForm990Data(fundId, {
      start: new Date(fiscalYear.fiscal_year_start),
      end: new Date(fiscalYear.fiscal_year_end)
    });
  }

  private async getFundTotals(fundId: string): Promise<FundTotals> {
    const query = `
      SELECT 
        a.category,
        SUM(CASE 
          WHEN a.type IN ('asset', 'expense') THEN je.debit - je.credit
          ELSE je.credit - je.debit
        END) as total
      FROM sponsfi.journal_entries je
      JOIN sponsfi.accounts a ON je.account_id = a.id
      WHERE je.fund_id = $1
      GROUP BY a.category
    `;

    const result = await this.executeQueryWithParams(query, [fundId]);
    const totals: FundTotals = {};
    
    result.rows.forEach((row: any) => {
      totals[row.category] = Number(row.total) || 0;
    });

    return totals;
  }

  private async getFundBudget(fundId: string): Promise<number> {
    const query = `
      SELECT COALESCE(SUM(amount), 0) as total_budget
      FROM sponsfi.fund_budgets
      WHERE fund_id = $1 AND status = 'approved'
    `;

    const result = await this.executeQueryWithParams(query, [fundId]);
    return Number(result.rows[0]?.total_budget || 0);
  }

  private async checkCompliance(
    fundId: string,
    requirement: ComplianceRequirement
  ): Promise<boolean> {
    const query = `
      WITH compliance_check AS (
        SELECT 
          CASE
            WHEN cr.type = 'documentation' THEN 
              EXISTS (
                SELECT 1 
                FROM sponsfi.fund_documents fd
                WHERE fd.fund_id = $1 
                AND fd.type = $2
                AND fd.status = 'approved'
              )
            WHEN cr.type = 'financial' THEN
              EXISTS (
                SELECT 1
                FROM sponsfi.fund_transactions ft
                WHERE ft.fund_id = $1
                AND ft.amount ${requirement.validation_rules.operator} $3
              )
            WHEN cr.type = 'reporting' THEN
              NOT EXISTS (
                SELECT 1
                FROM sponsfi.reporting_requirements rr
                WHERE rr.fund_id = $1
                AND rr.status = 'overdue'
              )
            ELSE true
          END as is_compliant
        FROM sponsfi.compliance_requirements cr
        WHERE cr.id = $4
      )
      SELECT is_compliant FROM compliance_check
    `;

    const params = [
      fundId,
      requirement.validation_rules.document_type,
      requirement.validation_rules.amount,
      requirement.id
    ];

    const result = await this.executeQueryWithParams<{ is_compliant: boolean }>(
      query,
      params
    );

    return result.rows[0]?.is_compliant ?? true;
  }
} 